// src/slices/moduleManagement/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModuleItem, ModuleModalState } from 'pages/ModuleManagement/types';
import {
  fetchModules,
  fetchModuleDetail,
  createNewModule,
  updateExistingModule,
  deleteModuleById,
} from './thunk';

interface ModuleManagementState {
  modules: ModuleItem[];
  selectedModule: ModuleItem | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  modalState: ModuleModalState;
}

const initialState: ModuleManagementState = {
  modules: [],
  selectedModule: null,
  loading: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
    delete: false,
  },
};

const moduleManagementSlice = createSlice({
  name: 'ModuleManagement',
  initialState,
  reducers: {
    setSelectedModule: (state, action: PayloadAction<ModuleItem | null>) => {
      state.selectedModule = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof ModuleModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModuleModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách modules
      .addCase(fetchModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.modules = payload.items || payload.data || payload || [];
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết module (dùng để mở form edit)
      .addCase(fetchModuleDetail.fulfilled, (state, action) => {
        state.selectedModule = action.payload as unknown as ModuleItem;
      })

      // Tạo mới
      .addCase(createNewModule.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createNewModule.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createNewModule.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Cập nhật
      .addCase(updateExistingModule.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateExistingModule.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.edit = false;
        state.selectedModule = null;
      })
      .addCase(updateExistingModule.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Xóa
      .addCase(deleteModuleById.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteModuleById.fulfilled, (state, action) => {
        state.submitting = false;
        state.modules = state.modules.filter(m => m.id !== action.payload);
        state.modalState.delete = false;
        state.selectedModule = null;
      })
      .addCase(deleteModuleById.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedModule,
  clearError,
  openModal,
  closeModal,
} = moduleManagementSlice.actions;

export default moduleManagementSlice.reducer;
